<template>
	<app-section class="menu-section flex flex-col">
		<div class="flex items-center" style="gap: 8px">
			<app-input name="search" icon="search" class="flex-1" v-model="searchValue" :debounceInterval="300" />
			<app-select id="statusSelect" class="w-32" :options="statusOptions" v-model="status" />
			<app-button
				icon="plus"
				class="btn btn-success transparent"
				@click="$emit('createProduct')"
				:disabled="!canEditMenu"
				>{{ $t('newProduct') }}</app-button
			>
		</div>
		<div class="flex" style="gap: 1px">
			<div class="categories-section">
				<div>
					<app-button
						v-if="innerCategories.length"
						icon="pencil"
						class="btn btn-secondary w-full"
						@click="$emit('editCategories')"
					>
						{{ $t('editCategories') }}
					</app-button>
				</div>
				<app-draggable
					class="flex flex-col"
					v-model="innerCategories"
					@moved="$emit('categoryMove', $event)"
					handle=".category-handle"
				>
					<template v-slot="{ item: cat }">
						<div
							:class="[
								'bg-gray-200 border-b-1 border-border-color p-2 mb-px cursor-pointer hover:bg-white flex items-center',
								{
									'bg-gray-300':
										selectedCategory.menuCategory &&
										selectedCategory.menuCategory.id === cat.menuCategory.id,
								},
							]"
							@click="categoryChange(cat)"
						>
							<app-icon
								v-if="cat.id !== -30 && canEditBusinessMenuCategory"
								icon="pencil"
								:class="`self-end cursor-pointer bg-white p-1 rounded-full`"
								@click="showMenuCategoryModal = true"
							/>
							<app-icon v-else icon="ellipsis-vert" class="cursor-move category-handle" />
							<span class="ml-2 font-semibold flex-1">{{ cat.menuCategory.categoryName }}</span>
							<!--							<app-icon icon="pencil" class="cursor-pointer ml-1" @click.stop="editMenuCategory(item)" />-->
						</div>
					</template>
				</app-draggable>
			</div>
			<div class="flex flex-1 bg-gray-100 overflow-x-auto">
				<div v-if="menuItems.length" class="flex-1 flex flex-col">
					<div class="flex bg-background px-3 border-b-1 border-border-color mb-px" style="gap: 8px">
						<span
							v-for="column in columns"
							:key="column.key"
							:class="['font-semibold p-2', column.customClass]"
						>
							{{ $t(`${column.key ? column.key : ''}`) }}
						</span>
						<span v-if="selectedCategory.id === -30" class="w-32 font-semibold p-2">Price Change</span>
						<span v-else class="w-32 font-semibold p-2">{{ $t('has_pending_change') }}</span>
						<span class="w-32 font-semibold p-2">Status</span>
						<span class="w-32 font-semibold p-2">{{ $t('actions') }}</span>
					</div>
					<app-draggable
						class="items-section"
						v-if="searchValue.length <= 2"
						v-model="menuItems"
						@moved="$emit('menuItemMove', $event, selectedCategory)"
					>
						<template v-slot="{ item }">
							<div
								class="flex-1 flex bg-gray-100 border-b-1 border-border-color mb-px hover:bg-white"
								style="gap: 8px"
							>
								<app-icon icon="ellipsis-vert" class="mt-3 -mr-3 pt-px cursor-move" />
								<div v-for="column in columns" :key="column.key" :class="['p-2', column.customClass]">
									<div v-if="column.key === 'image' && item[column.key]" class="w-18 h-15">
										<img
											:src="item[column.key]"
											class="w-18 h-15 object-cover cursor-pointer border-1 border-gray-200 rounded-lg"
											alt="ItemImage"
											@click="$emit('showImage', item[column.key])"
										/>
									</div>
									<span
										v-else-if="column.key === 'itemName'"
										class="cursor-pointer hover:underline hover:text-info"
										@click="editItem(item)"
									>
										{{ item[column.key] }}
									</span>
									<span v-else>
										{{ item[column.key] }}
									</span>
								</div>
								<div v-if="selectedCategory.id === -30" class="w-32 flex items-center">
									<app-checkbox :model-value="item.itemPriceChange" />
								</div>
								<div v-else class="w-32 flex items-center">
									<app-checkbox :model-value="item.hasPending" />
								</div>
								<div v-if="showRequestedChanges" class="w-32 flex items-center">
									<span
										class="w-4 h-4 rounded-full mr-1"
										:style="{ backgroundColor: getStatusColor(item) }"
									/>
									<span>{{ getStatus(item.status) }}</span>
								</div>
								<div v-else class="w-32 p-2">
									<app-switch
										offLabel="Inactive"
										onLabel="Active"
										:model-value="item.status"
										@change="onStatusChange(item, $event)"
									/>
								</div>
								<div class="w-32 flex p-2 items-start">
									<app-button
										icon="pencil"
										class="btn btn-info transparent small force-small text-13 mr-1"
										:loading="loading && item.id === selectedItem.id"
										@click="editItem(item)"
									/>
									<app-button
										icon="trash"
										class="btn btn-primary transparent small force-small text-13"
										:loading="loading && item.id === selectedItem.id"
										@click="deletePrompt(item)"
									/>
								</div>
							</div>
						</template>
					</app-draggable>
					<app-draggable
						v-if="searchValue.length > 2"
						v-model="searchedMenuItems"
						class="items-section"
						@moved="$emit('menuItemMove', $event, selectedCategory)"
					>
						<template v-slot="{ item }">
							<div
								class="flex-1 flex bg-gray-100 border-b-1 border-border-color mb-px hover:bg-white"
								style="gap: 8px"
							>
								<app-icon icon="ellipsis-vert" class="mt-3 -mr-3 pt-px cursor-move" />
								<div v-for="column in columns" :key="column.key" :class="['p-2', column.customClass]">
									<div v-if="column.key === 'image' && item[column.key]" class="w-18 h-15">
										<img
											:src="item[column.key]"
											class="w-18 h-15 object-cover cursor-pointer border-1 border-gray-200 rounded-lg"
											alt="ItemImage"
											@click="$emit('showImage', item[column.key])"
										/>
									</div>
									<span
										v-else-if="column.key === 'itemName'"
										class="cursor-pointer hover:underline hover:text-info"
										@click="editItemInSearch(item)"
									>
										{{ item[column.key] }}
									</span>
									<span v-else>
										{{ item[column.key] }}
									</span>
								</div>
								<div v-if="selectedCategory.id === -30" class="w-32 flex items-center">
									<app-checkbox :model-value="item.itemPriceChange" />
								</div>
								<div v-else class="w-32 flex items-center">
									<app-checkbox :model-value="item.hasPending" />
								</div>
								<div v-if="showRequestedChanges" class="w-32 flex items-center">
									<span
										class="w-4 h-4 rounded-full mr-1"
										:style="{ backgroundColor: getStatusColor(item) }"
									/>
									<span>{{ getStatus(item.status) }}</span>
								</div>
								<div v-else class="w-32 p-2">
									<app-switch
										offLabel="Inactive"
										onLabel="Active"
										:model-value="item.status"
										@change="onStatusChange(item, $event)"
									/>
								</div>
								<div class="w-32 flex p-2 items-start">
									<app-button
										icon="pencil"
										class="btn btn-info transparent small force-small text-13 mr-1"
										:loading="loading && item.id === selectedItem.id"
										@click="editItemInSearch(item)"
									/>
									<app-button
										icon="trash"
										class="btn btn-primary transparent small force-small text-13"
										:loading="loading && item.id === selectedItem.id"
										@click="deletePrompt(item)"
									/>
								</div>
							</div>
						</template>
					</app-draggable>
				</div>
				<div v-if="!menuItems.length && status" class="max-w-xl mx-auto">
					<new-product-info
						:category-name="selectedCategory.menuCategory && selectedCategory.menuCategory.categoryName"
						@click="$emit('createProduct')"
					/>
				</div>
				<div v-if="!menuItems.length && !status" class="flex-1 flex items-center justify-center">
					<span>
						No Inactive Items for "
						<b>{{ selectedCategory.menuCategory.categoryName }}</b>
						" category.
					</span>
				</div>
			</div>
			<menu-frequency-modal
				v-if="showMenuCategoryModal"
				:category="selectedCategory"
				@close="showMenuCategoryModal = false"
				@update:category="updateTime"
				@updateFrequency="selectedCategory.frequencies = $event"
				@submit="frequencySubmit"
				@horizontalChange="selectedCategory.showHorizontal = $event"
			>
			</menu-frequency-modal>
		</div>
	</app-section>
	<app-modal v-if="deleteModal" :title="`Delete ${selectedItem.itemName}?`" @close="cancelDelete">
		<div class="flex min-w-sm justify-end">
			<app-button icon="trash" class="flex-1 btn btn-primary mr-1" :loading="loading" @click="deleteItem">
				Delete
			</app-button>
			<app-button
				icon="cancel"
				class="flex-1 btn btn-primary transparent"
				:loading="loading"
				@click="cancelDelete"
			>
				Cancel
			</app-button>
		</div>
	</app-modal>
</template>

<script>
	import AppInput from '@/components/app/AppInput';
	import AppSelect from '@/components/app/AppSelect';
	import AppIcon from '@/components/app/AppIcon';
	import AppSwitch from '@/components/app/AppSwitch';
	import AppDraggable from '@/components/app/AppDraggable';
	import NewProductInfo from '@/components/sales/menu/NewProductInfo';
	import AppModal from '@/components/app/AppModal';
	import MenuService from '@/services/sales/MenuService';
	import business from '@/constants/business';
	import MenuFrequencyModal from '@/components/sales/menu/MenuFrequencyModal.vue';

	export default {
		name: 'MenuTable',
		emits: [
			'createProduct',
			'editProduct',
			'categoryChange',
			'statusChange',
			'editCategories',
			'categoryMove',
			'menuItemMove',
			'showImage',
			'getCategories',
		],
		components: {
			MenuFrequencyModal,
			AppModal,
			NewProductInfo,
			AppDraggable,
			AppSwitch,
			AppIcon,
			AppSelect,
			AppInput,
		},
		props: {
			categories: {
				type: Array,
				required: true,
			},
			loading: {
				type: Boolean,
				default: false,
			},
			businessId: {
				required: true,
			},
			menuType: {
				required: true,
			},
			canEditMenu: {
				type: Boolean,
				default: true,
			},
		},
		data: () => ({
			searchValue: '',
			status: -1,
			selectedCategory: {},
			selectedItem: {},
			deleteModal: false,
			allMenuItems: [],
			statusOptions: [
				{ label: 'All', value: -1 },
				{ label: 'Active', value: 1 },
				{ label: 'Inactive', value: 0 },
			],
			menuItemStatuses: business.menuItemStatuses,
			showMenuCategoryModal: false,
		}),
		computed: {
			showRequestedChanges() {
				return this.selectedCategory.menuCategory.id === -30;
			},
			columns: {
				get() {
					let columnArray = [
						{ key: 'image', label: 'Image', customClass: 'w-18' },
						{ key: 'itemName', label: 'Product', customClass: 'w-72' },
						{ key: 'itemDescription', label: 'Description', customClass: 'flex-1 w-64' },
						this.searchValue.length > 2
							? { key: 'itemCategoryName', label: 'Category', customClass: 'w-32' }
							: {},
						{ key: 'itemPrice', label: 'Price', customClass: 'w-32' },
						{ key: 'quantity', label: 'Inventory', customClass: 'w-32' },
					];
					return columnArray;
				},
			},
			innerCategories: {
				get() {
					if (!this.categories) {
						return [];
					}
					return this.categories;
				},
			},
			menuItems: {
				get() {
					return this.selectedCategory.menuCategory &&
						this.selectedCategory.menuCategory.menuItems &&
						this.selectedCategory.menuCategory.menuItems.length
						? this.selectedCategory.menuCategory.menuItems.filter(
								(menuItem) => menuItem.status === this.status || this.status === -1
						  )
						: [];
				},
			},
			searchedMenuItems: {
				get() {
					if (this.allMenuItems.length !== 0 && this.searchValue.length > 2) {
						let list = this.allMenuItems.filter(
							(menuItem) =>
								(menuItem.status === this.status || this.status === -1) &&
								(menuItem.itemName.toLowerCase().includes(this.searchValue.toLowerCase()) ||
									menuItem.itemDescription.toLowerCase().includes(this.searchValue.toLowerCase()))
						);
						return list;
					}
					return [];
				},
			},
			canEditBusinessMenuCategory() {
				return MenuService.canEditBusinessMenuCategory();
			},
		},
		methods: {
			getStatus(status) {
				return this.menuItemStatuses.find((_status) => _status.value === status).label;
			},
			getStatusColor(item) {
				let orderStatus = this.menuItemStatuses.find((_status) => _status.value === item.status);
				return orderStatus.color;
			},
			onStatusChange(item, status) {
				this.$emit('statusChange', this.selectedCategory, item, status);
				//ToDo after request
				// let index = this.menuItems.findIndex((_item) => _item.id === item.id);
				// this.menuItems[index].status = status;
			},
			updateTime(prop, value) {
				this.selectedCategory[prop] = value;
			},
			editItem(item) {
				this.selectedItem = item;
				this.$emit('editProduct', item);
			},
			deleteItem() {
				let endpoint = this.selectedCategory.menuCategory.id === -30 ? 'deletePendingProduct' : 'deleteProduct';
				MenuService[endpoint](this.businessId, this.menuType, this.selectedItem.id)
					.then(() => {
						const productIndex = this.selectedCategory.menuCategory.menuItems.findIndex(
							(i) => i.id === this.selectedItem.id
						);
						this.selectedCategory.menuCategory.menuItems.splice(productIndex, 1);
						this.cancelDelete();
					})
					.catch((e) => {
						this.$notificationService.error(e);
					});
			},
			getFormattedTime(date) {
				if (date.length >= 10) return this.$filters.getShortTime(date);
				else return date;
			},
			frequencySubmit() {
				MenuService.frequencySubmit(this.businessId, this.selectedCategory.id, {
					startDate: this.getFormattedTime(this.selectedCategory.startDate),
					endDate: this.getFormattedTime(this.selectedCategory.endDate),
					frequencies: this.selectedCategory.frequencies,
					showHorizontal: this.selectedCategory.showHorizontal,
				})
					.then(() => {
						this.$emit('getCategories');
					})
					.catch((e) => {
						this.$notificationService.error(e);
					});
			},
			deletePrompt(item) {
				this.selectedItem = item;
				this.deleteModal = true;
			},
			cancelDelete() {
				this.selectedItem = {};
				this.deleteModal = false;
			},
			editMenuCategory(menuCategory) {
				console.log(menuCategory);
			},
			categoryChange(cat) {
				this.selectedCategory = cat;
				this.searchValue = '';
				this.$emit('categoryChange', cat);
			},
			getAllItems() {
				for (let category of this.categories) {
					if (category.menuCategory.menuItems !== undefined) {
						for (let menuItem of category.menuCategory.menuItems) {
							this.allMenuItems.push(
								JSON.parse(
									JSON.stringify({
										...menuItem,
										itemCategoryName: category.menuCategory.categoryName,
										itemCategory: { id: category.id },
									})
								)
							);
						}
					}
				}
			},
			editItemInSearch(item) {
				this.$emit('categoryChange', item.itemCategory);
				this.editItem(item);
			},
		},
		mounted() {
			this.selectedCategory = { ...this.categories[0] };
			this.getAllItems();
		},
		watch: {
			loading(val) {
				if (!val) {
					this.selectedItem = {};
				}
			},
		},
	};
</script>

<style lang="scss" scoped>
	.menu-section {
		height: calc(100vh - 180px);
	}
	.categories-section {
		@apply border-r-1 border-border-color flex flex-col overflow-y-auto;
		max-height: calc(100vh - 270px);
		min-width: 150px;
	}
	.items-section {
		@apply flex flex-col overflow-y-auto;
		max-height: calc(100vh - 313px);
	}
</style>
